/* global globalThis */
import React, { useEffect, useRef, useState } from "react";
import styles from "./LeadForm.module.css";
import ContactForm from "../../components/ContactForm/ContactForm";
import ReusableModal from "../../components/ReusableModal/ReusableModal";
import ReusableSnackbar from "../../components/ReusableSnackbar/ReusableSnackbar";
import api, { baseURL } from "../../Utils/Network";
import marcusevans from "../../Assets/ME_Logo_NEGATIVE.png";
import marcusEvansBlack from "../../Assets/ME_Logo_BLACK.svg";

const LeadForm = () => {
  const [modalOpenForUser, setmodalOpenForUser] = useState(false);

  // bulk upload modal + file + loading
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  // drag state (for styling)
  const [isDragging, setIsDragging] = useState(false);

  // file input ref
  const fileInputRef = useRef(null);

  // snackbar for bulk upload
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState({
    UserType: "",
  });

  // Mandatory columns for bulk upload
  const MANDATORY_COLUMNS = [
    "Your Email address",
    "Firstname",
    "Lastname",
    "Company Name",
    "Job Title",
    "Country",
    "Types",
    "Event Code",
  ];

  // ✅ Sonar S7776 fix: use Set for existence checks

  const allowedExtensions = new Set(["csv", "xls", "xlsx", "xlsm"]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleuserType = () => {
    if (formData.UserType) {
      localStorage.setItem("userType", formData.UserType);
      setmodalOpenForUser(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData({
      ...formData,
      [name]: newValue,
    });
  };

  const handleOpenModel = () => {
    setmodalOpenForUser(true);
  };

  const handleOpenBulkModal = () => {
    setBulkFile(null);
    setBulkModalOpen(true);
  };

  const handleCloseBulkModal = () => {
    if (!bulkUploading) {
      setBulkModalOpen(false);
      setIsDragging(false);
    }
  };

  const validateFile = (file) => {
    if (!file) return false;

    const ext = file.name.split(".").pop()?.toLowerCase();

    // ✅ Sonar S7776 fix: use allowedExtensions.has()
    if (!ext || !allowedExtensions.has(ext)) {
      showSnackbar(
        "Invalid file type. Please upload .csv, .xls, .xlsx, or .xlsm.",
        "error"
      );
      return false;
    }

    return true;
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      setBulkFile(file);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const openFilePicker = () => {
    if (bulkUploading) return;
    fileInputRef.current?.click();
  };

  // drag/drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer?.files?.[0];
    if (file && validateFile(file)) {
      setBulkFile(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/bulk/template", undefined, {
        responseType: "blob",
      });

      const blob = response.data;
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Try to get filename from Content-Disposition header
      const disposition = response.headers?.["content-disposition"];
      const filenameMatch = /filename[^;=\n]*=["']?([^"';\n]*)["']?/.exec(disposition ?? "");
      a.download = filenameMatch?.[1] ?? "HubSpot_Contacts_Template.xlsm";

      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackbar(error?.message || "Failed to download template.", "error");
    }
  };

  const handleBulkUploadSubmit = async (e) => {
    e.preventDefault();

    if (!bulkFile) {
      showSnackbar("Please choose a file before uploading.", "error");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("file", bulkFile, bulkFile.name);

    try {
      setBulkUploading(true);

      const uploadUrl = `${baseURL}/bulk/upload`;
      const token = localStorage.getItem("access_token");

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formDataToSend,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      let responseData = {};
      try {
        responseData = await response.json();
      } catch {
        responseData = {};
      }

      if (!response.ok) {
        const backendMessage =
          responseData?.detail ||
          responseData?.message ||
          responseData?.error ||
          "Upload failed";

        throw new Error(backendMessage);
      }

      showSnackbar(
        responseData?.detail ||
        responseData?.message ||
        "Contacts uploaded successfully!",
        "success"
      );

      setBulkFile(null);
      setBulkModalOpen(false);
    } catch (error) {
      showSnackbar(error?.message || "Something went wrong.", "error");
    } finally {
      setBulkUploading(false);
    }
  };

  useEffect(() => {
    const storedUserType = localStorage.getItem("userType");

    if (storedUserType) {
      setFormData((prev) => ({ ...prev, UserType: storedUserType }));
      return;
    }
    setmodalOpenForUser(true);
  }, []);

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.headerImageCon}>
          <img
            src={marcusevans}
            alt="marcus evans"
            className={styles.headerImage}
          />
        </div>

        <div className={styles.headerButtons}>
          <button
            className={`${styles.btn} ${styles.bulkBtn}`}
            type="button"
            onClick={handleOpenBulkModal}
          >
            Bulk Upload
          </button>

          <button
            className={`${styles.btn} ${styles.bulkBtn}`}
            type="button"
            onClick={handleOpenModel}
          >
            Edit User Type
          </button>
        </div>
      </div>

      <div className={styles.ContactContainer}>
        <ContactForm />
      </div>

      <ReusableModal
        isOpen={modalOpenForUser}
        handleClose={() => setmodalOpenForUser(false)}
        maxWidth={60}
        disableBackdropClick={true}
      >
        <div className={styles.modalOverlay}>
          <div className={styles.WrapperimgCon}>
            <div className={styles.imgContainer}>
              <img
                src={marcusEvansBlack}
                alt="marcus evans"
                className={styles.meImage}
              />
            </div>
          </div>

          <div className={styles.modalContent}>
            <p className={styles.contactContainertext}>
              Please specify which area of the business you are most involved in:
            </p>
            <select
              name="UserType"
              value={formData?.UserType}
              onChange={handleChange}
              className={styles.dropdown}
            >
              <option value="" disabled>
                Please select an option
              </option>
              <option value="DA">DA, for example "DA Sales, DRM"</option>
              <option value="SP">SP, for example "SP Sales"</option>
              <option value="PROD">PROD, for example "Producer"</option>
              <option value="MKT">
                MKT, for example "Marketing Executive, Digital Marketing"
              </option>
              <option value="ADE">ADE, for example "Account Development Executive"</option>
              <option value="RES-DA-COMP">
                RES-DA-COMP, for example “Summit DA Research”
              </option>
              <option value="RES-DA-PAID">
                RES-DA-PAID, for example “Conference Delegate Research”
              </option>
              <option value="RES-SP">
                RES-SP, for example “Sponsorship Research”
              </option>
              <option value="RES-ONLINE-EVENTS">
                RES-ONLINE-EVENTS, for example "Online Events (Webinars)"
              </option>
              <option value="OTHER">OTHER, for example "Finance, IT"</option>
            </select>
            <div className={styles.wrapperButton}>
              <button onClick={handleuserType} className={styles.closeButton}>
                Submit
              </button>
            </div>
          </div>
        </div>
      </ReusableModal>

      <ReusableModal
        isOpen={bulkModalOpen}
        handleClose={handleCloseBulkModal}
        maxWidth={60}
      >
        <div className={styles.bulkModal}>
          <h2 className={styles.bulkTitle}>Upload your file</h2>

          <div className={styles.mandatoryCard}>
            <div className={styles.mandatoryHeader}>
              <span className={styles.infoIcon}>*</span>
              <p className={styles.mandatoryTitle}>
                Mandatory columns required in the upload file
              </p>
            </div>

            <div className={styles.mandatoryList}>
              {MANDATORY_COLUMNS.map((col) => (
                <span key={col} className={styles.mandatoryChip}>
                  {col}
                </span>
              ))}
            </div>

            <p className={styles.mandatoryNote}>
              All listed columns are required. If any column is missing, the upload
              will fail.
            </p>
          </div>

          <form onSubmit={handleBulkUploadSubmit}>
            <div className={styles.bulkSectionLabel} style={{ marginTop: "10px" }}>
              Contacts file
            </div>

            {/* ✅ native interactive element (Sonar S6819 fix already) */}
            <button
              type="button"
              className={`${styles.bulkDropZone} ${isDragging ? styles.dragActive : ""}`}
              onClick={openFilePicker}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              aria-label="Upload contacts file. Drag and drop a file here, or click to choose a file."
              disabled={bulkUploading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M12 17v-6" />
                <polyline points="9.5 13 12 10.5 14.5 13" />
              </svg>

              <div className={styles.bulkText}>
                <span>Drag and drop or </span>
                <span className={styles.bulkChooseFile}>choose a file</span>
                <span> to upload your Contacts.</span>

                <input
                  ref={fileInputRef}
                  id="bulk-file-input"
                  name="file"
                  type="file"
                  accept=".csv,.xls,.xlsx,.xlsm,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                  className={styles.hiddenFileInput}
                  onChange={handleBulkFileChange}
                />

                <div className={styles.bulkSupported}>
                  All .csv, .xlsx, .xls, and .xlsm file types are supported.
                </div>
              </div>

              {bulkFile && (
                <div className={styles.bulkFileName}>
                  Selected file: {bulkFile.name}
                </div>
              )}
            </button>

            <div className={styles.bulkFooter}>
              <div className={styles.bulkDownloadWrapper}>
                <button
                  type="button"
                  className={styles.bulkExampleLink}
                  onClick={handleDownloadTemplate}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download example file
                </button>
                <div className={styles.bulkMacroWarning}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  This template contains macros that provide smart validation (multi-select PSI, auto date formatting, invalid value highlighting). You may need to enable macros after downloading for all features to work.
                </div>
              </div>

              <div className={styles.bulkButtons}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleCloseBulkModal}
                  disabled={bulkUploading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className={styles.buttonEnabled}
                  disabled={bulkUploading}
                >
                  {bulkUploading ? "Uploading..." : "Submit"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReusableModal>

      <ReusableSnackbar
        open={snackbar.open}
        handleClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default LeadForm;